import { create } from 'zustand';
import apiClient from '../lib/api';

export type ServiceOrderStatus = 'draft' | 'submitted' | 'approved' | 'resource-fulfillment' | 'closed';
export type BenchStatus = 'available' | 'partial' | 'reserved' | 'allocated' | 'unavailable';

export interface ServiceOrder {
  id: string;
  clientName: string;
  projectName: string;
  soId: string;
  positionTitle: string;
  requiredCount: number;
  department: string;
  experienceRequired: string;
  mandatorySkills: string[];
  preferredSkills: string[];
  location: string;
  workMode: 'remote' | 'hybrid' | 'onsite';
  budgetRange: string;
  expectedDate: string;
  duration: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  hiringManager: string;
  justification: string;
  status: ServiceOrderStatus;
  createdAt: string;
}

export interface BenchTalent {
  id: string;
  employeeId: string;
  name: string;
  designation: string;
  totalExperience: number;
  primarySkills: string[];
  secondarySkills: string[];
  certifications: string[];
  currentLocation: string;
  previousProjects: string[];
  availabilityDate: string;
  onBenchSince: string;
  costRate: number;
  performanceScore: number;
  status: BenchStatus;
  avatar?: string;
  resumeUrl?: string;
}

export interface WorkforceState {
  serviceOrders: ServiceOrder[];
  benchTalents: BenchTalent[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchServiceOrders: () => Promise<void>;
  createServiceOrder: (so: Partial<ServiceOrder>) => Promise<void>;
  updateServiceOrderStatus: (id: string, status: ServiceOrderStatus) => Promise<void>;
  deleteServiceOrder: (id: string) => Promise<void>;

  fetchBench: () => Promise<void>;
  getMatches: (soId: string) => Promise<BenchTalent[]>;
  allocateTalent: (soId: string, talentId: string, managerApproval?: boolean) => Promise<void>;
  routeToTA: (soId: string) => Promise<void>;
  addToBench: (talent: Partial<BenchTalent>) => Promise<void>;
  updateBenchTalent: (id: string, data: Partial<BenchTalent>) => Promise<void>;
  deleteBenchTalent: (id: string) => Promise<void>;
}

export const useWorkforceStore = create<WorkforceState>((set, get) => ({
  serviceOrders: [],
  benchTalents: [],
  isLoading: false,
  error: null,

  fetchServiceOrders: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/people/workforce/orders');
      set({ serviceOrders: (response.data?.data ?? response.data), isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  createServiceOrder: async (so) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.post('/people/workforce/orders', so);
      set(state => ({ serviceOrders: [(response.data?.data ?? response.data), ...state.serviceOrders], isLoading: false }));
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  updateServiceOrderStatus: async (id, status) => {
    try {
      const response = await apiClient.patch(`/people/workforce/orders/${id}/status`, { status });
      set(state => ({
        serviceOrders: state.serviceOrders.map(so => so.id === id ? (response.data?.data ?? response.data) : so)
      }));
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  fetchBench: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/people/workforce/bench');
      set({ benchTalents: (response.data?.data ?? response.data), isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  getMatches: async (soId) => {
    const so = get().serviceOrders.find(s => s.id === soId);
    if (!so) return [];
    
    // Skill and experience matching logic
    return get().benchTalents.filter(talent => {
      // Handle skills (check if mandatory list has any intersection with talent's primary skills)
      const skillMatch = talent.primarySkills.some(s => 
          so.mandatorySkills.some(req => req.toLowerCase().includes(s.toLowerCase()))
      );

      // Handle experience numeric conversion (e.g. "8+ Years" -> 8)
      const reqExp = parseInt(so.experienceRequired.match(/\d+/)?.at(0) || '0');
      const expMatch = talent.totalExperience >= reqExp;

      return (talent.status === 'available' || talent.status === 'partial') && (skillMatch || expMatch);
    });
  },

  allocateTalent: async (soId, talentId, managerApproval) => {
    try {
      await apiClient.patch(`/people/workforce/bench/${talentId}`, { status: 'allocated' });
      await apiClient.patch(`/people/workforce/orders/${soId}/status`, { status: 'resource-fulfillment' });
      set(state => ({
        benchTalents: state.benchTalents.map(t => t.id === talentId ? { ...t, status: 'allocated' } : t),
        serviceOrders: state.serviceOrders.map(so => so.id === soId ? { ...so, status: 'resource-fulfillment' } : so)
      }));
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  routeToTA: async (soId) => {
      try {
        await apiClient.patch(`/people/workforce/orders/${soId}/status`, { status: 'resource-fulfillment' });
        set(state => ({
            serviceOrders: state.serviceOrders.map(so => so.id === soId ? { ...so, status: 'resource-fulfillment' } : so)
        }));
      } catch (error: any) {
        set({ error: error.message });
      }
  },

  addToBench: async (talent) => {
      set({ isLoading: true, error: null });
      try {
        const response = await apiClient.post('/people/workforce/bench', talent);
        set(state => ({ benchTalents: [(response.data?.data ?? response.data), ...state.benchTalents], isLoading: false }));
      } catch (error: any) {
        set({ error: error.message, isLoading: false });
      }
  },

  updateBenchTalent: async (id, data) => {
      try {
        const response = await apiClient.patch(`/people/workforce/bench/${id}`, data);
        set(state => ({
            benchTalents: state.benchTalents.map(t => t.id === id ? (response.data?.data ?? response.data) : t)
        }));
      } catch (error: any) {
        set({ error: error.message });
      }
  },

  deleteServiceOrder: async (id) => {
      try {
        await apiClient.delete(`/people/workforce/orders/${id}`);
        set(state => ({ serviceOrders: state.serviceOrders.filter(so => so.id !== id) }));
      } catch (error: any) {
        set({ error: error.message });
        throw error;
      }
  },

  deleteBenchTalent: async (id) => {
      try {
        await apiClient.delete(`/people/workforce/bench/${id}`);
        set(state => ({ benchTalents: state.benchTalents.filter(t => t.id !== id) }));
      } catch (error: any) {
        set({ error: error.message });
        throw error;
      }
  }
}));
